import { useId } from 'react';
import { X, Search, Link, Unlink, Users, Building2, Briefcase, FileText, MessageSquare, Clock } from 'lucide-react';
import ContatosTab from './ContatosTab';
import ReceitaFederalTab from './ReceitaFederalTab';
import SituacaoCadastralBadge from './SituacaoCadastralBadge';
import { FiliaisTab, OportunidadesTab, InteracoesTab, TimelineTab } from './conta-form/ContaFormTabs';
import ParentContaCombobox from './conta-form/ParentContaCombobox';
import { useContaForm } from './conta-form/useContaForm';

const PORTES = ['MEI', 'ME', 'EPP', 'Médio', 'Grande'];
const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

// ZYN-311: refatorado de um único arquivo de 1019 linhas — estado e lógica
// foram para useContaForm, as abas de detalhe para conta-form/ContaFormTabs,
// e o combobox de matriz para conta-form/ParentContaCombobox. Este componente
// é só o orquestrador: layout do modal, abas e o form de "Dados".
export default function ContaFormModal({ conta, onClose, onSaved, initialTab = 'dados' }) {
  const formFieldIds = useId();
  const idNome = `${formFieldIds}-nome`;
  const idRazao = `${formFieldIds}-razao`;
  const idCnpj = `${formFieldIds}-cnpj`;
  const idEsn = `${formFieldIds}-esn`;

  const {
    form, set, setForm,
    activeTab, setActiveTab,
    showChildModal, setShowChildModal, childPrefills, setChildPrefills,
    esns,
    loading, cepLoading, cnpjLoading, cnpjMsg,
    error,
    existingConta,
    linkedLeads, leadSearch, leadResults, leadSearchLoading, handleLeadSearch, linkLead, unlinkLead,
    parentCandidates, parentSelected, setParentSelected,
    parentQuery, setParentQuery, parentResults, parentOpen, setParentOpen, parentBoxRef,
    fetchCnpj,
    handleSubmit,
  } = useContaForm({ conta, onSaved, initialTab });

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#00DBFF]';

  const TABS = [
    { key: 'dados',         label: 'Dados',         icon: FileText },
    { key: 'contatos',      label: 'Contatos',      icon: Users },
    { key: 'filiais',       label: 'Filiais',       icon: Building2 },
    { key: 'oportunidades', label: 'Oportunidades', icon: Briefcase },
    { key: 'interacoes',    label: 'Interações',    icon: MessageSquare },
    { key: 'timeline',      label: 'Timeline',      icon: Clock },
    { key: 'receita',       label: 'Receita Federal', icon: Building2 },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className={`bg-white rounded-xl shadow-xl w-full ${conta ? 'max-w-4xl' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-[#002233] rounded-t-xl">
          <h2 className="text-white font-semibold flex items-center gap-2">
            {conta ? `Conta — ${conta.nome}` : 'Nova Conta'}
            {conta && <SituacaoCadastralBadge situacao={conta.situacao_cadastral} />}
          </h2>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white"><X size={18} /></button>
        </div>

        {conta && (
          <div className="flex gap-1 px-6 pt-3 border-b border-gray-200 bg-gray-50">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = activeTab === t.key;
              return (
                <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                    active
                      ? 'border-[#00DBFF] text-[#002233]'
                      : 'border-transparent text-gray-500 hover:text-[#002233]'
                  }`}>
                  <Icon size={13} />
                  {t.label}
                </button>
              );
            })}
          </div>
        )}

        {conta && activeTab === 'filiais'       && <div className="p-6"><FiliaisTab contaId={conta.id} /></div>}
        {conta && activeTab === 'contatos'      && <div className="p-6"><ContatosTab contaId={conta.id} /></div>}
        {conta && activeTab === 'oportunidades' && <div className="p-6"><OportunidadesTab contaId={conta.id} /></div>}
        {conta && activeTab === 'interacoes'    && <div className="p-6"><InteracoesTab contaId={conta.id} /></div>}
        {conta && activeTab === 'timeline'      && <div className="p-6"><TimelineTab contaId={conta.id} /></div>}
        {conta && activeTab === 'receita'       && (
          <div className="p-6">
            <ReceitaFederalTab
              conta={form.id ? form : { ...conta, ...form }}
              onEnriched={(enrichedData) => {
                setForm(f => ({
                  ...f,
                  ...enrichedData,
                  razao_social:     enrichedData.razao_social     || f.razao_social,
                  nome_fantasia:    enrichedData.nome_fantasia    || f.nome_fantasia,
                  cidade:           enrichedData.cidade           || f.cidade,
                  estado:           enrichedData.estado           || f.estado,
                  endereco_rua:     enrichedData.logradouro       || f.endereco_rua,
                  endereco_numero:  enrichedData.numero           || f.endereco_numero,
                  endereco_bairro:  enrichedData.bairro           || f.endereco_bairro,
                  endereco_cep:     enrichedData.cep              || f.endereco_cep,
                  telefone_empresa: enrichedData.telefone         || f.telefone_empresa,
                }));
                // NÃO fechar o modal após enriquecer — o usuário precisa ver os dados atualizados
              }}
              onCreateConta={(prefills) => {
                setChildPrefills(prefills);
                setShowChildModal(true);
              }}
            />
          </div>
        )}

        {(!conta || activeTab === 'dados') && (
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}

          {/* Informações Básicas */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">Informações Básicas</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor={idNome} className="block text-xs font-medium text-gray-600 mb-1">Nome Fantasia *</label>
                <input id={idNome} value={form.nome} onChange={e => set('nome', e.target.value)} required className={inputClass} />
              </div>
              <div>
                <label htmlFor={idRazao} className="block text-xs font-medium text-gray-600 mb-1">Razão Social</label>
                <input id={idRazao} value={form.razao_social} onChange={e => set('razao_social', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label htmlFor={idCnpj} className="block text-xs font-medium text-gray-600 mb-1">CNPJ *</label>
                <div className="flex gap-2">
                  <input id={idCnpj} value={form.cnpj}
                    onChange={e => set('cnpj', e.target.value)}
                    placeholder="00.000.000/0001-00"
                    required
                    className={`${inputClass} flex-1`} />
                  <button type="button" onClick={fetchCnpj} disabled={cnpjLoading}
                    title="Buscar dados na Receita Federal"
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-gray-600 disabled:opacity-50 transition-colors flex-shrink-0">
                    {cnpjLoading ? <span className="text-xs">…</span> : <Search size={15} />}
                  </button>
                </div>
                {cnpjMsg && (
                  <p className={`text-xs mt-1 ${cnpjMsg.startsWith('✓') ? 'text-green-600' : 'text-amber-600'}`}>
                    {cnpjMsg}
                  </p>
                )}
                {/* ZYN-306: conta já existente com este CNPJ — abrir em vez de duplicar */}
                {!conta && existingConta && (
                  <div className="mt-2 rounded-lg bg-amber-50 border border-amber-300 px-3 py-2 text-xs">
                    <p className="font-semibold text-amber-800">
                      Conta já cadastrada: {existingConta.nome}
                      {existingConta.esn_nome ? ` (ESN ${existingConta.esn_nome})` : ''}
                    </p>
                    <a href={`/contas?openConta=${existingConta.id}`}
                      className="text-amber-700 underline hover:text-amber-900 font-semibold">
                      Abrir conta existente →
                    </a>
                  </div>
                )}
              </div>
              <div>
                <label htmlFor={idEsn} className="block text-xs font-medium text-gray-600 mb-1">ESN Responsável</label>
                <select id={idEsn} value={form.esn_id} onChange={e => set('esn_id', e.target.value)}
                  className={`${inputClass} bg-white`}>
                  <option value="">— Sem ESN —</option>
                  {esns.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                </select>
              </div>
              <ParentContaCombobox
                form={form} set={set}
                parentSelected={parentSelected} setParentSelected={setParentSelected}
                parentQuery={parentQuery} setParentQuery={setParentQuery}
                parentOpen={parentOpen} setParentOpen={setParentOpen}
                parentCandidates={parentCandidates} parentResults={parentResults}
                parentBoxRef={parentBoxRef}
                inputClass={inputClass}
              />
            </div>
          </div>

          {/* Endereço */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">Endereço</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Rua</label>
                <input value={form.endereco_rua} onChange={e => set('endereco_rua', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Número</label>
                <input value={form.endereco_numero} onChange={e => set('endereco_numero', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bairro</label>
                <input value={form.endereco_bairro} onChange={e => set('endereco_bairro', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  CEP {cepLoading && <span className="text-[#00DBFF]">buscando…</span>}
                </label>
                <input value={form.endereco_cep} onChange={e => set('endereco_cep', e.target.value)}
                  placeholder="00000-000" className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Cidade</label>
                <input value={form.cidade} onChange={e => set('cidade', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Estado</label>
                <select value={form.estado} onChange={e => set('estado', e.target.value)}
                  className={`${inputClass} bg-white`}>
                  <option value="">—</option>
                  {UFS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Classificação */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">Classificação</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Segmento</label>
                <input value={form.segmento} onChange={e => set('segmento', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Porte</label>
                <select value={form.porte} onChange={e => set('porte', e.target.value)}
                  className={`${inputClass} bg-white`}>
                  <option value="">—</option>
                  {PORTES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Contato */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">Contato</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Telefone</label>
                <input value={form.telefone_empresa} onChange={e => set('telefone_empresa', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">E-mail</label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} className={inputClass} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Site</label>
                <input value={form.site} onChange={e => set('site', e.target.value)}
                  placeholder="https://" className={inputClass} />
              </div>
            </div>
          </div>

          {/* Conta Vinculada — só em modo edição */}
          {conta && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">
                Leads Vinculados
              </h3>

              {/* Leads já vinculados */}
              {linkedLeads.length > 0 && (
                <div className="mb-3 space-y-2">
                  {linkedLeads.map(lead => (
                    <div key={lead.id}
                      className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {lead.titulo || lead.razao_social}
                        </p>
                        <p className="text-xs text-gray-500">
                          {lead.funnel_type === 'new_names' ? 'New Names' : 'Base'} · {lead.stage}
                        </p>
                      </div>
                      <button type="button" onClick={() => unlinkLead(lead)}
                        title="Desvincular"
                        className="ml-3 text-gray-400 hover:text-red-500 flex-shrink-0 transition-colors">
                        <Unlink size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Search to link new lead */}
              <div className="relative">
                <div className="flex gap-2 items-center">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                      value={leadSearch}
                      onChange={e => handleLeadSearch(e.target.value)}
                      placeholder="Buscar lead por nome, empresa ou CNPJ…"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#00DBFF]"
                    />
                  </div>
                  {leadSearchLoading && <span className="text-xs text-gray-400">buscando…</span>}
                </div>

                {leadResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                    {leadResults.map(lead => (
                      <button key={lead.id} type="button" onClick={() => linkLead(lead)}
                        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#00DBFF]/10 text-left transition-colors">
                        <Link size={13} className="text-[#00DBFF] flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {lead.titulo || lead.razao_social}
                          </p>
                          <p className="text-xs text-gray-500">
                            {lead.razao_social} · {lead.funnel_type === 'new_names' ? 'New Names' : 'Base'} · {lead.stage}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Observações */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-200">Observações</h3>
            <textarea value={form.obs} onChange={e => set('obs', e.target.value)} rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#00DBFF] resize-none" />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 text-sm bg-[#00DBFF] text-[#002233] font-semibold rounded-lg hover:bg-[#00DBFF]/80 disabled:opacity-50 transition-colors">
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
        )}
        {showChildModal && (
          <ContaFormModal
            conta={childPrefills ? { cnpj: childPrefills.cnpj, parent_conta_id: childPrefills.parent_conta_id, nome: '' } : null}
            onClose={() => { setShowChildModal(false); setChildPrefills(null); }}
            onSaved={() => {
              setShowChildModal(false);
              setChildPrefills(null);
              onSaved?.();
            }}
          />
        )}
      </div>
    </div>
  );
}
